import React, { useState, useCallback, createContext, useContext } from 'react';

/**
 * Toast notification system — provides non-intrusive feedback for actions.
 * 
 * Usage:
 *   const toast = useToast();
 *   toast.success('Event created!');
 *   toast.error('Something went wrong');
 *   toast.info('DNS copied to clipboard');
 * 
 * Wrap your app in <ToastProvider> and use the useToast() hook anywhere.
 */

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 4000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const toast = {
    success: (msg, duration) => addToast(msg, 'success', duration),
    error: (msg, duration) => addToast(msg, 'error', duration),
    info: (msg, duration) => addToast(msg, 'info', duration),
    warn: (msg, duration) => addToast(msg, 'warn', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Toast container — fixed bottom-right */}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none" style={{ maxWidth: '380px' }}>
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => setToasts(prev => prev.filter(x => x.id !== t.id))} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

const TOAST_STYLES = {
  success: {
    bg: 'rgba(34,197,94,0.08)',
    border: 'rgba(34,197,94,0.2)',
    icon: '✅',
    textColor: 'rgba(34,197,94,0.9)',
  },
  error: {
    bg: 'rgba(239,68,68,0.08)',
    border: 'rgba(239,68,68,0.2)',
    icon: '❌',
    textColor: 'rgba(239,68,68,0.9)',
  },
  info: {
    bg: 'rgba(59,130,246,0.08)',
    border: 'rgba(59,130,246,0.2)',
    icon: 'ℹ️',
    textColor: 'rgba(59,130,246,0.9)',
  },
  warn: {
    bg: 'rgba(245,158,11,0.08)',
    border: 'rgba(245,158,11,0.2)',
    icon: '⚠️',
    textColor: 'rgba(245,158,11,0.9)',
  },
};

function ToastItem({ toast, onDismiss }) {
  const style = TOAST_STYLES[toast.type] || TOAST_STYLES.info;

  return (
    <div
      className="pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl backdrop-blur-xl shadow-2xl animate-toast-in"
      style={{
        background: style.bg,
        border: `1px solid ${style.border}`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px ${style.border}`,
      }}
    >
      <span className="text-sm leading-none mt-0.5 flex-shrink-0">{style.icon}</span>
      <p className="text-sm text-white/80 leading-snug flex-1">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="text-white/25 hover:text-white/50 transition-colors text-xs leading-none mt-0.5 flex-shrink-0 cursor-pointer"
      >
        ✕
      </button>
    </div>
  );
}
